import ReactModal from "react-modal";
import React, { useEffect, useState } from "react";
import axios from "axios";
import {themes} from "prism-react-renderer";
import {HilightedCode} from "../utils.js";
import {HELIOCLOUDS3LINK} from './../constants.js';

/**
 * Modal dialog that displays HelioCloud S3 instructions for a given URL.
 *
 * @param prop.productKey cdaweb product key of dataset
 * @param prop.isHeliocloudS3ModalOpen Whether to display the modal
 * @param props.setHeliocloudS3ModalOpen Sets the value of isHeliocloudModalOpen
 * @return JSX
 */
export function HeliocloudS3InstructionsModal(props) {
    const {heliocloudS3ProductUrl, productKey, isHeliocloudS3ModalOpen, setHeliocloudS3ModalOpen} = props;
    const heliocloudS3UrlObj = new URL(HELIOCLOUDS3LINK);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [state, setState] = useState({pythonCodeCopied: false});

    const pythonCode =                   `dataset_id = '${data?.id || "{id (e.g )}"}'\n` +
                  `start = '${data?.start || "{start time (YYYY-MM-ddTHH:MM:SS.000Z)}"}'\n` +
                  `stop =   '${data?.stop || "{end time (YYYY-MM-ddTHH:MM:SS.000Z)}"}'\n` +
                  "fr=cloudcatalog.CloudCatalog('s3://gov-nasa-hdrl-data1/')\n" +
                  "filelist = fr.request_cloud_catalog(dataset_id, start_date=start, stop_date=stop)\n" +
                  "\n" +
                  "#Example usage:\n" +
                  "fr.stream_uri(filelist, lambda s3_uri, start, stop, filesize: MYCODE(s3_uri, start, stop, filesize))`\n";

    const copyPythonToClipboard = () => {
        setState({...state, pythonCodeCopied: true});
        navigator.clipboard.writeText(pythonCode);
    };

    const modalStyle = {
        overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.75)'
        },
        content: {
            position: 'absolute',
            top: '10%',
            left: '20%',
            right: '20%',
            bottom: '20%',
            border: '1px solid #ccc',
            background: '#fff',
            height: '635px',
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch',
            borderRadius: '4px',
            outline: 'none',
            padding: '20px'
        }
    };

    const onRequestClose = () => {
        const newObj = {};
        newObj[HELIOCLOUDS3LINK] = false;
        setHeliocloudS3ModalOpen({...isHeliocloudS3ModalOpen, ...newObj});
    };


useEffect(() => {
    if (!productKey) return;
    const controller = new AbortController();

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        setData(null);

        try {
            const response = await axios.get("/heliodata/app/get_heliocloud_s3_metadata", {
                params: { id: productKey },
                signal: controller.signal,
            });

            setData(response.data);

        } catch (err) {
            if (axios.isCancel(err)) return;

            if (err.response) {
                switch (err.response.status) {
                    case 400:
                        setError("Invalid request: Dataset product key is required.");
                        break;
                    case 404:
                        setError(err.response.data?.error || "Dataset not found.");
                        break;
                    case 503:
                        setError("Could not connect to the data source. Please try again later.");
                        break;
                    case 500:
                        setError("A server error occurred. Please try again later.");
                        break;
                    default:
                        setError(`Unexpected error: ${err.response.status}`);
                }
            } else if (err.request) {
                setError("No response from server. Check your network connection.");
            } else {
                setError(`Error: ${err.message}`);
            }
        } finally {
            setLoading(false);
        }
    };

    fetchData();
    return () => controller.abort();
}, [productKey]);

    return (
        <ReactModal
          style={modalStyle}
          isOpen={isHeliocloudS3ModalOpen}
          onRequestClose={onRequestClose}>
          <div className="heliocloud-instructions-modal-inner">
            <div className="heliocloud-instructions-modal-header">HelioCloud S3 Download</div>

            <div className="heliocloud-instructions-modal-download">
              <table>
                <tr>
                  <td className="heliocloud-instructions-modal-connection-left-col">URL</td>
                  <td className="heliocloud-instructions-modal-connection-right-col">
                    <div className="heliocloud-instructions-modal-connection-item">
                      <a href={heliocloudS3ProductUrl} target="_blank" rel="noopener noreferrer">
                        <code>{heliocloudS3ProductUrl}</code>
                      </a>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="heliocloud-instructions-modal-connection-left-col">Hostname</td>
                  <td className="heliocloud-instructions-modal-connection-right-col">
                    <div className="heliocloud-instructions-modal-connection-item">
                        <code>{heliocloudS3UrlObj.hostname}</code>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="heliocloud-instructions-modal-connection-left-col">Index Prefix</td>
                  <td className="heliocloud-instructions-modal-connection-right-col">
                    <div className="heliocloud-instructions-modal-connection-item">
                      <code>{data?.index}</code>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="heliocloud-instructions-modal-connection-left-col">Id</td>
                  <td className="heliocloud-instructions-modal-connection-right-col">
                    <div className="heliocloud-instructions-modal-connection-item">
                      <code>{data?.id}</code>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="heliocloud-instructions-modal-connection-left-col">Start</td>
                  <td className="heliocloud-instructions-modal-connection-right-col">
                    <div className="heliocloud-instructions-modal-connection-item">
                      <code>{data?.start}</code>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="heliocloud-instructions-modal-connection-left-col">Stop</td>
                  <td className="heliocloud-instructions-modal-connection-right-col">
                    <div className="heliocloud-instructions-modal-connection-item">
                      <code>{data?.stop}</code>
                    </div>
                  </td>
                </tr>
              </table>

              {error && (
                <div className="heliocloud-instructions-modal-error">{error}</div>
              )}

            </div>
            <br />
            <div className="heliocloud-instructions-modal-header">Python Instructions</div>
              <div>
                <button onClick= {() => {copyPythonToClipboard()}} className="heliocloud-instruction-blue-button">
                  {state.pythonCodeCopied ? "Copied" : "Copy to Clipboard"}
                </button>
              </div>
              <HilightedCode
                className="access-by-api-code"
                language="python"
                white-space= "pre-wrap"
                overflow-wrap= "break-word"
                code= {pythonCode}
                theme={themes.duotoneLight}>
              </HilightedCode>
            <button onClick={onRequestClose} className="ftp-instruction-blue-button">
              Close
            </button>
            <br />
          </div>
        </ReactModal>
    );
}
